import React, { useEffect, useRef } from 'react';
import 'altcha';

interface AltchaWidgetProps {
  onToken?: (token: string | null) => void;
  className?: string;
}

type AltchaVerifyEvent = CustomEvent<{ payload?: string | null }>;
type AltchaStateEvent = CustomEvent<{ state?: string }>;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export function AltchaWidget({ onToken, className }: AltchaWidgetProps) {
  const widgetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const node = widgetRef.current;
    const handleVerify = (event: Event) => {
      const payload = (event as AltchaVerifyEvent).detail?.payload || null;
      onToken?.(payload);
    };
    const handleReset = () => onToken?.(null);
    const cleanupOverlay = () => {
      document
        .querySelectorAll(".altcha-overlay, .altcha-overlay-backdrop")
        .forEach((element) => element.remove());
    };

    if (node) {
      node.addEventListener('verified', handleVerify as EventListener);
      node.addEventListener(
        'statechange',
        ((e: AltchaStateEvent) => {
          if (e.detail?.state === 'expired') {
            onToken?.(null);
          }
        }) as EventListener,
      );
      node.addEventListener('reset', handleReset as EventListener);
    }
    return () => {
      if (node) {
        node.removeEventListener('verified', handleVerify as EventListener);
        node.removeEventListener('statechange', handleReset as EventListener);
        node.removeEventListener('reset', handleReset as EventListener);
      }
      cleanupOverlay();
    };
  }, [onToken]);

  const challengeUrl = `${SUPABASE_URL}/functions/v1/altcha-challenge`;

  return React.createElement('altcha-widget', {
    ref: widgetRef as React.Ref<unknown>,
    className,
    challengeurl: challengeUrl,
    hidefooter: true,
  });
}
